import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import LoadingText from 'components/common/loading-text';
import ToggleSwitch from 'components/inputs/toggle-switch';
import Tooltip from 'components/layout/tooltip';
import { ExistingSystemComponentType } from 'data/types';
import { useUpdateComponentReplacingMutation } from 'features/api/project-api';
import {
  updateExistingSystem,
  updateExistingSystemComponent
} from 'features/structure';
import { useState } from 'react';
import { deleteEquipmentComponentPhotos } from 'utils/equipment-images';
import { systemComponentIsValid } from 'utils/systems';
import ExistingSystemComponentForm from './existing-system-component-form';

type ExistingSystemComponentProps = {
  component: ExistingSystemComponentType;
  activeExistingSystem: number;
};

export default function ExistingSystemComponentComponent({
  component,
  activeExistingSystem
}: ExistingSystemComponentProps) {
  const dispatch = useAppDispatch();
  const { existingSystems } = useAppSelector((state) => state.structure);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [updateComponentReplacing] = useUpdateComponentReplacingMutation();
  const [deleteQueued, setDeleteQueued] = useState(false);

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const deleteComponent = () => {
    deleteEquipmentComponentPhotos(component);
    dispatch(
      updateExistingSystem({
        ...existingSystems[activeExistingSystem],
        components: existingSystems[activeExistingSystem].components.filter(
          (c) => c.uniqueId !== component.uniqueId
        )
      })
    );
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteComponent();
    setDeleteQueued(false);
  };

  const classes = clsx(
    'w-full flex flex-col gap-3 rounded-xl border-2 bg-gray-100',
    systemComponentIsValid(component) ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='font-semibold'>
            {component.type || 'New Component'}
          </div>
          <label className='flex cursor-pointer select-none items-center gap-2'>
            <ToggleSwitch
              value={component.replacing}
              onChange={(value) => {
                dispatch(
                  updateExistingSystemComponent({
                    ...component,
                    replacing: value
                  })
                );
                updateComponentReplacing({
                  uniqueId: component.uniqueId,
                  replacing: value
                });
              }}
            />
            <span>Replacing</span>
          </label>
          {component.analyzing && (
            <span className='text-gray-500'>
              <LoadingText text='Analyzing' />
            </span>
          )}
          {!component.analyzing && !component.type && (
            <span className='text-red-500'>Error Loading Data</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Configure</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={
                  deleteQueued ? 'Click Again to Confirm' : 'Delete Component'
                }
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='pb-2'>
            <ExistingSystemComponentForm component={component} />
          </div>
        )}
      </div>
    </div>
  );
}
